import type { DateFormatKey, TimeFormatKey } from '../types';

export const LOGGER_NAME = 'date-translation';

export const DATE_FORMAT_VARIANT: DateFormatKey = {
  GENERAL_DATE_FORMAT: 'MMM D, YYYY',
  DATE_HEADER: 'MMMM D, YYYY • dddd',
  MONTH_YEAR: 'MMMM, YYYY',
  FULL_MONTH_DAY: 'MMMM D',
  WEEKDAY_MONTH_DAY: 'dddd, MMMM D',
  WEEKDAY_MONTH_DAY_YEAR: 'dddd, MMMM D, YYYY',
  NUMERIC_DATE: 'MM/DD/YYYY',
  SHORT_MONTH_ORDINAL_YEAR: 'MMM Do, YYYY',
  DAY_SHORT_MONTH_YEAR: 'DD MMM YYYY',
  DAY_MONTH_SHORT: 'DD MMM',
  NUMERIC_DATE_DMY: 'DD-MM-YYYY',
  ISO_DATE_FORMAT: 'YYYY-MM-DD',
  DAY_FULL_MONTH_SHORT_YEAR: "D MMMM' YY",
  FULL_MONTH_DAY_YEAR: 'MMMM D, YYYY',
  FULL_MONTH_ORDINAL_YEAR: 'MMMM Do, YYYY',
  SHORT_MONTH_YEAR: 'MMM, YYYY',
  FULL_MONTH_YEAR: 'MMMM YYYY',
  DAY_ABBR_MONTH: 'DD-MMM',
  NUMERIC_DATE_COMPACT: 'YYYYMMDD',
  ABBR_MONTH_DAY: 'MMM D',
  ABBR_WEEKDAY_ABBR_MONTH_DAY_YEAR: 'ddd, MMM D, YYYY',
  WEEKDAY_ABBR_MONTH_DAY_YEAR: 'dddd, MMM D, YYYY',
  ABBR_WEEKDAY_DAY_ABBR_MONTH_YEAR: 'ddd D MMM, YYYY',
  NUMERIC_DATE_SHORT: 'MM/DD/YY',
  HOUR_AMPM_ABBR_MONTH_DAY: 'h a, MMM D',
  ABBR_MONTH_DAY_SHORT_YEAR: "MMM D, 'YY",
};

export const MOMENT_CONSTANTS = {
  DD: 'DD',
  MM: 'MM',
  MMM: 'MMM',
  MMMM: 'MMMM',
  YY: 'YY',
  YYYY: 'YYYY',
  D: 'D',
  dd: 'dd',
  ddd: 'ddd',
  dddd: 'dddd',
  HH: 'HH',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  ss: 'ss',
  a: 'a',
  A: 'A',
  DAY: 'day',
  DAYS: 'days',
  WEEK: 'week',
  WEEKS: 'weeks',
  MONTH: 'month',
  MONTHS: 'months',
  YEAR: 'year',
  YEARS: 'years',
  HOUR: 'hour',
  HOURS: 'hours',
  MINUTE: 'minute',
  MINUTES: 'minutes',
  SECOND: 'second',
  SECONDS: 'seconds',
} as const;

export const TIME_FORMAT_VARIANT: TimeFormatKey = {
  standard: { '12h': 'h:mm a', '24h': 'HH:mm' },
  upperMeridian: { '12h': 'h:mm A', '24h': 'HH:mm' },
  paddedUpperMeridian: { '12h': 'hh:mm A', '24h': 'HH:mm' },
  withSeconds: { '12h': 'h:mm:ss a', '24h': 'HH:mm:ss' },
  gmtOffSet: { '12h': 'hh:mm', '24h': 'hh:mm' },
  hourOnly: { '12h': 'h a', '24h': 'HH' },
  noAmPm: { '12h': 'h:mm', '24h': 'HH:mm' },
  paddedNoAmPm: { '12h': 'hh:mm', '24h': 'HH:mm' },
  finnish: { '12h': 'h.mm a', '24h': 'HH.mm' },
  germanTimeFormat: { '12h': 'h:mm a ', '24h': 'HH:mm [Uhr]' },
  fixed12h: { '12h': 'h:mm a', '24h': 'h:mm a' },
  fixed24h: { '12h': 'HH:mm', '24h': 'HH:mm' },
  fixed12hWithSeconds: { '12h': 'h:mm:ss a', '24h': 'h:mm:ss a' },
  fixed24hWithSeconds: { '12h': 'HH:mm:ss', '24h': 'HH:mm:ss' },
};

export const IS_24_HOUR_FORMAT_KEY: string = 'is24HourFormat';
export const DEFAULT_LOCALE: string = 'en';
export const DEFAULT_TIME_KEY = 'standard' as const;
export const HOUR_FORMAT_24H = '24h' as const;
export const HOUR_FORMAT_12H = '12h' as const;

// All standard time formats from the TIME_FORMAT_VARIANT
const ALL_TIME_FORMATS: string[] = [];
for (const pair of Object.values(TIME_FORMAT_VARIANT)) {
  ALL_TIME_FORMATS.push(pair['12h'], pair['24h']);
}

// Available keys for time formats
export const AVAILABLE_TIME_FORMAT_KEYS: Set<string> = new Set(Object.keys(TIME_FORMAT_VARIANT));

// All standard date formats from the DATE_FORMAT_VARIANT
const ALL_DATE_FORMATS = Object.values(DATE_FORMAT_VARIANT);

// Override date formats that are not part of the standard locale formats but are used in specific locales
const LOCALE_DATE_OVERRIDES: string[] = [
  'D. MMM YYYY',
  'D MMM YYYY',
  'YYYY. MMM D.',
  'D [de] MMM [de] YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'MM/DD/YY',
  'D. MMMM',
  'D MMMM',
  'MMM D.',
  'D [de] MMMM',
  'YYYY. MMMM',
  'MMMM [de] YYYY',
  'dddd D. MMMM',
  'dddd [den] D. MMMM',
  'dddd D MMMM',
  'dddd, D. MMMM',
  'dddd, D MMMM',
  'YYYY. MMMM D., dddd',
  'dddd, D [de] MMMM',
  'D. MMMM YYYY • dddd',
  'D MMMM YYYY • dddd',
  'YYYY. MMMM D. • dddd',
  'D [de] MMMM [de] YYYY • dddd',
];

// A set of all available formats combining time formats, date formats, and locale-specific date formats
export const AVAILABLE_FORMATS: Set<string> = new Set([
  ...ALL_TIME_FORMATS,
  ...ALL_DATE_FORMATS,
  ...LOCALE_DATE_OVERRIDES,
]);
